import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheckIcon, UploadIcon, CheckCircleIcon, AlertCircleIcon, ChevronRightIcon, LinkedinIcon, GithubIcon, UserIcon, BuildingIcon } from 'lucide-react';
interface VerificationFlowProps {
  userType: 'mentor' | 'mentee';
}
const VerificationFlow: React.FC<VerificationFlowProps> = ({
  userType
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [documents, setDocuments] = useState<{
    idDocument: File | null;
    professionalDocument: File | null;
  }>({
    idDocument: null,
    professionalDocument: null
  });
  const handleFileUpload = (type: 'idDocument' | 'professionalDocument') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setDocuments(prev => ({
      ...prev,
      [type]: file
    }));
  };
  const handleSubmit = () => {
    // Handle verification submission
    navigate(userType === 'mentor' ? '/mentor-dashboard' : '/mentee-dashboard');
  };
  const renderStep = () => {
    switch (step) {
      case 1:
        return <div data-id="element-2106">
            <h3 className="text-lg font-semibold mb-4" data-id="element-2107">
              Identity Verification
            </h3>
            <div className="space-y-4" data-id="element-2108">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2109">
                <h4 className="font-medium text-sm mb-2" data-id="element-2110">Accepted Documents</h4>
                <ul className="text-sm text-gray-600 space-y-1" data-id="element-2111">
                  <li data-id="element-2112">• Government-issued ID</li>
                  <li data-id="element-2113">• Passport</li>
                  <li data-id="element-2114">• Driver's License</li>
                </ul>
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center" data-id="element-2115">
                {documents.idDocument ? <div className="flex items-center justify-center space-x-2" data-id="element-2116">
                    <CheckCircleIcon className="text-green-500" size={20} data-id="element-2117" />
                    <span className="text-sm text-gray-600" data-id="element-2118">
                      {documents.idDocument.name}
                    </span>
                  </div> : <div data-id="element-2119">
                    <UploadIcon className="mx-auto h-8 w-8 text-gray-400" data-id="element-2120" />
                    <p className="mt-2 text-sm text-gray-600" data-id="element-2121">
                      Upload your identification document
                    </p>
                    <input type="file" className="hidden" id="id-upload" accept="image/*,.pdf" onChange={handleFileUpload('idDocument')} data-id="element-2122" />
                    <label htmlFor="id-upload" className="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-2123">
                      Select File
                    </label>
                  </div>}
              </div>
            </div>
          </div>;
      case 2:
        return <div data-id="element-2124">
            <h3 className="text-lg font-semibold mb-4" data-id="element-2125">
              Professional Verification
            </h3>
            <div className="space-y-4" data-id="element-2126">
              <div className="grid grid-cols-1 gap-4" data-id="element-2127">
                <button className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50" data-id="element-2128">
                  <div className="flex items-center" data-id="element-2129">
                    <LinkedinIcon className="text-blue-600 mr-3" size={24} data-id="element-2130" />
                    <div className="text-left" data-id="element-2131">
                      <div className="font-medium" data-id="element-2132">LinkedIn</div>
                      <div className="text-sm text-gray-500" data-id="element-2133">
                        Verify with your professional profile
                      </div>
                    </div>
                  </div>
                  <ChevronRightIcon size={20} className="text-gray-400" data-id="element-2134" />
                </button>
                <button className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50" data-id="element-2135">
                  <div className="flex items-center" data-id="element-2136">
                    <BuildingIcon className="text-gray-600 mr-3" size={24} data-id="element-2137" />
                    <div className="text-left" data-id="element-2138">
                      <div className="font-medium" data-id="element-2139">Work Email</div>
                      <div className="text-sm text-gray-500" data-id="element-2140">
                        Verify with your company email
                      </div>
                    </div>
                  </div>
                  <ChevronRightIcon size={20} className="text-gray-400" data-id="element-2141" />
                </button>
                {userType === 'mentor' && <button className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50" data-id="element-2142">
                    <div className="flex items-center" data-id="element-2143">
                      <GithubIcon className="text-gray-800 mr-3" size={24} data-id="element-2144" />
                      <div className="text-left" data-id="element-2145">
                        <div className="font-medium" data-id="element-2146">GitHub</div>
                        <div className="text-sm text-gray-500" data-id="element-2147">
                          Verify with your developer profile
                        </div>
                      </div>
                    </div>
                    <ChevronRightIcon size={20} className="text-gray-400" data-id="element-2148" />
                  </button>}
              </div>
            </div>
          </div>;
      case 3:
        return <div data-id="element-2149">
            <h3 className="text-lg font-semibold mb-4" data-id="element-2150">Review & Submit</h3>
            <div className="space-y-4" data-id="element-2151">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2152">
                <h4 className="font-medium mb-3" data-id="element-2153">Verification Summary</h4>
                <div className="space-y-3" data-id="element-2154">
                  <div className="flex items-start" data-id="element-2155">
                    <UserIcon size={20} className="text-gray-400 mr-2" data-id="element-2156" />
                    <div data-id="element-2157">
                      <p className="text-sm font-medium" data-id="element-2158">Identity Document</p>
                      <p className="text-xs text-gray-500" data-id="element-2159">
                        Uploaded and ready for review
                      </p>
                    </div>
                    <CheckCircleIcon size={20} className="text-green-500 ml-auto" data-id="element-2160" />
                  </div>
                  <div className="flex items-start" data-id="element-2161">
                    <BuildingIcon size={20} className="text-gray-400 mr-2" data-id="element-2162" />
                    <div data-id="element-2163">
                      <p className="text-sm font-medium" data-id="element-2164">
                        Professional Verification
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2165">
                        LinkedIn profile connected
                      </p>
                    </div>
                    <CheckCircleIcon size={20} className="text-green-500 ml-auto" data-id="element-2166" />
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4" data-id="element-2167">
                <div className="flex" data-id="element-2168">
                  <AlertCircleIcon size={20} className="text-blue-500 mr-2" data-id="element-2169" />
                  <div data-id="element-2170">
                    <p className="text-sm text-blue-800 font-medium" data-id="element-2171">
                      What happens next?
                    </p>
                    <p className="text-sm text-blue-600 mt-1" data-id="element-2172">
                      Our team will review your documents within 1-2 business
                      days. You'll receive an email once the verification is
                      complete.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-gray-50 p-4" data-id="element-2173">
      <div className="max-w-md mx-auto" data-id="element-2174">
        <div className="bg-white rounded-xl shadow-md overflow-hidden" data-id="element-2175">
          <div className="bg-purple-600 px-4 py-3 text-white flex items-center" data-id="element-2176">
            <ShieldCheckIcon className="mr-2" size={20} data-id="element-2177" />
            <h2 className="text-lg font-semibold" data-id="element-2178">Profile Verification</h2>
          </div>
          <div className="w-full bg-gray-200 h-1" data-id="element-2179">
            <div className="bg-purple-600 h-1 transition-all duration-300" style={{
            width: `${step / 3 * 100}%`
          }} data-id="element-2180" />
          </div>
          <div className="p-4" data-id="element-2181">
            {renderStep()}
            <div className="flex justify-between mt-6" data-id="element-2182">
              {step > 1 ? <button onClick={() => setStep(step - 1)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-id="element-2183">
                  Back
                </button> : <div data-id="element-2184" />}
              {step < 3 ? <button onClick={() => setStep(step + 1)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-2185">
                  Continue
                </button> : <button onClick={handleSubmit} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-2186">
                  Submit for Review
                </button>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default VerificationFlow;